import {
  Zap,
  Calendar,
  CheckSquare,
  Users,
  Settings,
  FileText,
  TrendingUp,
  AlertTriangle,
  Search,
  Plus,
  BarChart3,
  Clock,
  GitBranch,
  Shield,
  CreditCard,
  Database,
  Workflow,
  UserCog,
  FileSearch,
  Activity,
  LayoutDashboard,
  // New icons for expanded domains
  DollarSign,
  Target,
  Award,
  Trophy,
  Flame,
  MessageSquare,
  Heart,
  Building2,
  PieChart,
  LineChart,
  Folder,
  BookOpen,
  HelpCircle,
  Star,
  UserPlus,
  Network,
  // Universal actions icons
  Lightbulb,
  Brain
} from "lucide-react";
import type { AgentContext, AppContext, QuickAction, QuickActionGroup, SystemState } from "./types";

/**
 * Quick Actions Service
 * Generates context-aware, role-aware quick actions for the AI agent
 */
export class QuickActionsService {
  /**
   * Generate quick actions based on current context, role, and system state
   */
  generateActions(
    agentContext: AgentContext,
    currentContext: AppContext,
    systemState?: SystemState
  ): QuickAction[] {
    const actions: QuickAction[] = [];

    // Add domain-specific actions based on route
    const domainActions = this.getDomainActions(agentContext, currentContext);
    actions.push(...domainActions);

    // Add state-based actions (e.g., "3 overdue tasks")
    if (systemState) {
      const stateActions = this.getStateBasedActions(systemState, agentContext);
      actions.push(...stateActions);
    }

    // Add universal actions available everywhere
    const universalActions = this.getUniversalActions(agentContext);
    actions.push(...universalActions);

    // Filter by permissions
    const filteredActions = this.filterByPermissions(actions, agentContext);

    // Sort by priority
    return filteredActions.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Get domain-specific actions based on current route/context
   */
  private getDomainActions(agentContext: AgentContext, _currentContext: AppContext): QuickAction[] {
    const domain = agentContext.expertise?.domain;
    
    switch (domain) {
      // Core domains
      case 'tasks':
        return this.getTasksActions(agentContext);
      case 'calendar':
        return this.getCalendarActions(agentContext);
      case 'workflows':
        return this.getWorkflowsActions(agentContext);
      case 'dashboard':
        return this.getDashboardActions(agentContext);
      
      // CRM & Sales
      case 'sales-portal':
        return this.getSalesPortalActions(agentContext);
      case 'clients':
        return this.getClientsActions(agentContext);
      case 'opportunities':
        return this.getOpportunitiesActions(agentContext);
      
      // Team & Users
      case 'users':
        return this.getUsersActions(agentContext);
      case 'team':
        return this.getTeamActions(agentContext);
      
      // Gamification & Engagement
      case 'gamification':
      case 'leaderboards':
      case 'achievements':
      case 'seasons':
        return this.getGamificationActions(agentContext);
      
      // Community
      case 'community':
        return this.getCommunityActions(agentContext);
      
      // Analytics & Reports
      case 'progress-reports':
      case 'analytics':
        return this.getAnalyticsActions(agentContext);
      case 'audit':
        return this.getAuditActions(agentContext);
      
      // Financial
      case 'subscriptions':
        return this.getSubscriptionsActions(agentContext);
      case 'accounting':
        return this.getAccountingActions(agentContext);
      
      // Time & Productivity
      case 'time-management':
        return this.getTimeManagementActions(agentContext);
      
      // Content & Docs
      case 'docs':
      case 'content':
        return this.getDocsActions(agentContext);
      
      // Storage
      case 'attachments':
        return this.getAttachmentsActions(agentContext);
      
      // General purpose (fallback)
      case 'general':
        return this.getGeneralActions(agentContext);
      
      default:
        return [];
    }
  }

  /**
   * Tasks domain actions
   */
  private getTasksActions(agentContext: AgentContext): QuickAction[] {
    const actions: QuickAction[] = [
      {
        id: 'tasks-show-overdue',
        label: 'Show Overdue Tasks',
        description: 'List all overdue tasks with suggestions to catch up',
        icon: AlertTriangle,
        category: 'diagnostic',
        permission: 'canCreateTodos',
        prompt: 'List my overdue tasks with priority levels and suggest a plan to catch up. Include estimated time for each task.',
        keywords: ['overdue', 'late', 'delayed', 'urgent', 'priority'],
        priority: 9,
        domain: 'tasks',
        estimatedTime: '< 5 seconds'
      },
      {
        id: 'tasks-today-summary',
        label: "Today's Task Summary",
        description: 'Get a summary of tasks due today',
        icon: CheckSquare,
        category: 'analysis',
        permission: 'canCreateTodos',
        prompt: 'Give me a summary of all tasks due today, organized by priority. Include completion status and estimated time remaining.',
        keywords: ['today', 'summary', 'daily', 'schedule'],
        priority: 8,
        domain: 'tasks'
      },
      {
        id: 'tasks-create-sprint',
        label: 'Plan This Week',
        description: 'Create a weekly sprint plan',
        icon: GitBranch,
        category: 'creation',
        permission: 'canCreateTodos',
        prompt: 'Help me plan this week by analyzing my upcoming tasks and suggesting a sprint plan with realistic daily goals.',
        keywords: ['sprint', 'plan', 'week', 'organize'],
        priority: 7,
        domain: 'tasks'
      },
      {
        id: 'tasks-priority-analysis',
        label: 'Analyze Task Priority',
        description: 'Review and suggest priority adjustments',
        icon: TrendingUp,
        category: 'analysis',
        permission: 'canEditTodos',
        prompt: 'Analyze my current task priorities and suggest any adjustments based on deadlines, dependencies, and importance.',
        keywords: ['priority', 'importance', 'organize', 'rank'],
        priority: 6,
        domain: 'tasks'
      },
      {
        id: 'tasks-blocked-items',
        label: 'Find Blocked Tasks',
        description: 'Identify tasks that might be blocking progress',
        icon: Shield,
        category: 'diagnostic',
        permission: 'canCreateTodos',
        prompt: 'Identify any blocked or stalled tasks and suggest actions to unblock them. Look for dependencies and bottlenecks.',
        keywords: ['blocked', 'stuck', 'dependencies', 'bottleneck'],
        priority: 7,
        domain: 'tasks'
      }
    ];

    // Add bulk operations for users with edit permissions
    if (agentContext.capabilities.canEditTodos) {
      actions.push({
        id: 'tasks-bulk-update',
        label: 'Bulk Task Operations',
        description: 'Get help with bulk task updates',
        icon: Database,
        category: 'management',
        permission: 'canEditTodos',
        prompt: 'I need help performing bulk operations on tasks. What bulk actions are available and how can I use them efficiently?',
        keywords: ['bulk', 'batch', 'multiple', 'mass'],
        priority: 5,
        domain: 'tasks'
      });
    }

    return actions;
  }

  /**
   * Calendar domain actions
   */
  private getCalendarActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'calendar-diagnostic',
        label: 'Run Calendar Diagnostic',
        description: 'Check calendar integration and permissions',
        icon: Activity,
        category: 'diagnostic',
        prompt: 'Run a comprehensive calendar integration diagnostic. Check OAuth tokens, API permissions, meeting link generation, and required scopes. Report any issues found.',
        keywords: ['diagnostic', 'troubleshoot', 'calendar', 'sync', 'integration'],
        priority: 9,
        domain: 'calendar',
        isNew: true,
        estimatedTime: '5-10 seconds'
      },
      {
        id: 'calendar-sync-status',
        label: 'Check Sync Status',
        description: 'Verify calendar synchronization',
        icon: Calendar,
        category: 'diagnostic',
        prompt: 'Check the current calendar synchronization status. Show last sync time, any errors, and upcoming scheduled syncs.',
        keywords: ['sync', 'status', 'calendar', 'connection'],
        priority: 8,
        domain: 'calendar'
      },
      {
        id: 'calendar-upcoming-events',
        label: 'Show Upcoming Events',
        description: 'List upcoming calendar events',
        icon: Clock,
        category: 'navigation',
        prompt: 'Show me all upcoming events for the next 7 days, organized by day. Include meeting links and participants.',
        keywords: ['upcoming', 'events', 'meetings', 'schedule'],
        priority: 7,
        domain: 'calendar'
      },
      {
        id: 'calendar-availability',
        label: 'Check Availability',
        description: 'Find available time slots',
        icon: Search,
        category: 'analysis',
        prompt: 'Analyze my calendar and find available time slots for the next 2 weeks. Show blocks of at least 1 hour.',
        keywords: ['availability', 'free time', 'open slots', 'schedule'],
        priority: 6,
        domain: 'calendar'
      },
      {
        id: 'calendar-conflicts',
        label: 'Find Scheduling Conflicts',
        description: 'Identify overlapping events',
        icon: AlertTriangle,
        category: 'diagnostic',
        prompt: 'Scan my calendar for any scheduling conflicts or overlapping events. Suggest resolutions.',
        keywords: ['conflicts', 'overlap', 'double-booked'],
        priority: 8,
        domain: 'calendar'
      }
    ];
  }

  /**
   * Users domain actions
   */
  private getUsersActions(agentContext: AgentContext): QuickAction[] {
    const actions: QuickAction[] = [
      {
        id: 'users-overview',
        label: 'User Summary',
        description: 'Get an overview of system users',
        icon: Users,
        category: 'analysis',
        permission: 'canSeeAdminData',
        prompt: 'Provide a summary of all users in the system. Include total count, active users, recent signups, and role distribution.',
        keywords: ['users', 'summary', 'overview', 'stats'],
        priority: 7,
        domain: 'users'
      },
      {
        id: 'users-permission-check',
        label: 'Check User Permissions',
        description: 'Verify permissions for a specific user',
        icon: Shield,
        category: 'diagnostic',
        permission: 'canManageUsers',
        prompt: 'Help me check and verify permissions for a specific user. What permissions should I check and how?',
        keywords: ['permissions', 'access', 'roles', 'security'],
        priority: 8,
        domain: 'users'
      }
    ];

    if (agentContext.capabilities.canManageUsers) {
      actions.push(
        {
          id: 'users-role-guide',
          label: 'Role Management Guide',
          description: 'Learn about user roles and permissions',
          icon: UserCog,
          category: 'navigation',
          permission: 'canManageUsers',
          prompt: 'Explain the different user roles (Architect, Admin, Moderator, User, Viewer) and their permissions. Help me understand when to assign each role.',
          keywords: ['roles', 'permissions', 'guide', 'access'],
          priority: 6,
          domain: 'users'
        },
        {
          id: 'users-audit-activity',
          label: 'Audit User Activity',
          description: 'Review recent user actions',
          icon: FileSearch,
          category: 'analysis',
          permission: 'canViewAuditLogs',
          prompt: 'Show me recent user activity and help me identify any unusual patterns or security concerns.',
          keywords: ['audit', 'activity', 'logs', 'security'],
          priority: 7,
          domain: 'users'
        }
      );
    }

    return actions;
  }

  /**
   * Workflows domain actions — aligned with operational SOPs
   */
  private getWorkflowsActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'workflows-overview',
        label: 'Workflow Overview',
        description: 'List all available workflows',
        icon: Workflow,
        category: 'navigation',
        prompt: 'Show me all available automation workflows. Organize them by category and highlight the most commonly used ones.',
        keywords: ['workflows', 'automation', 'overview'],
        priority: 7,
        domain: 'workflows'
      },
      {
        id: 'workflows-optimization',
        label: 'Workflow Optimization Tips',
        description: 'Get suggestions to improve workflows',
        icon: TrendingUp,
        category: 'analysis',
        permission: 'canPerformAdminActions',
        prompt: 'Analyze current workflows and suggest optimizations. Look for redundancies, bottlenecks, and opportunities for consolidation.',
        keywords: ['optimize', 'improve', 'efficiency', 'automation'],
        priority: 6,
        domain: 'workflows'
      },
      {
        id: 'workflows-create-guide',
        label: 'Create New Workflow',
        description: 'Guide for building workflows',
        icon: Plus,
        category: 'creation',
        permission: 'canPerformAdminActions',
        prompt: 'Guide me through creating a new automation workflow. What are the steps, best practices, and common patterns?',
        keywords: ['create', 'new', 'build', 'automation'],
        priority: 8,
        domain: 'workflows'
      },
      {
        id: 'workflows-daily-standup',
        label: 'Run Daily Standup',
        description: 'Generate a daily standup summary from tasks and activity',
        icon: Clock,
        category: 'productivity',
        prompt: 'Run the daily standup workflow: summarize tasks completed yesterday, tasks planned for today, and any blockers across the team.',
        keywords: ['standup', 'daily', 'status', 'morning', 'sop'],
        priority: 9,
        domain: 'workflows'
      },
      {
        id: 'workflows-client-onboarding',
        label: 'Client Onboarding SOP',
        description: 'Walk through the client onboarding process',
        icon: UserPlus,
        category: 'creation',
        prompt: 'Walk me through the client onboarding SOP step by step: initial intake, CRM setup, project creation, welcome email, and first meeting scheduling.',
        keywords: ['onboarding', 'client', 'intake', 'new client', 'sop'],
        priority: 8,
        domain: 'workflows'
      },
      {
        id: 'workflows-escalation',
        label: 'Escalation Process',
        description: 'Follow the escalation SOP for issues',
        icon: AlertTriangle,
        category: 'diagnostic',
        prompt: 'Guide me through the escalation SOP: identify the issue severity, determine the right escalation path, notify stakeholders, and track resolution.',
        keywords: ['escalation', 'issue', 'urgent', 'sop', 'severity'],
        priority: 7,
        domain: 'workflows'
      },
      {
        id: 'workflows-weekly-review',
        label: 'Weekly Review & Retrospective',
        description: 'Generate weekly performance review and retrospective',
        icon: BarChart3,
        category: 'analysis',
        prompt: 'Run the weekly review workflow: summarize completed tasks, team velocity, missed deadlines, and key wins. Then suggest improvements for next week.',
        keywords: ['weekly', 'review', 'retrospective', 'retro', 'sop'],
        priority: 7,
        domain: 'workflows'
      }
    ];
  }

  /**
   * Subscriptions domain actions
   */
  private getSubscriptionsActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'subscriptions-cost-analysis',
        label: 'Subscription Cost Analysis',
        description: 'Analyze subscription spending',
        icon: CreditCard,
        category: 'analysis',
        prompt: 'Provide a detailed analysis of subscription costs. Show monthly/yearly totals, upcoming renewals, and identify potential savings.',
        keywords: ['cost', 'spending', 'analysis', 'budget'],
        priority: 8,
        domain: 'subscriptions'
      },
      {
        id: 'subscriptions-upcoming-renewals',
        label: 'Upcoming Renewals',
        description: 'List subscriptions renewing soon',
        icon: AlertTriangle,
        category: 'analysis',
        prompt: 'Show me all subscriptions renewing in the next 30 days. Include cost, renewal date, and whether auto-renewal is enabled.',
        keywords: ['renewal', 'upcoming', 'due', 'expiring'],
        priority: 9,
        domain: 'subscriptions'
      },
      {
        id: 'subscriptions-by-category',
        label: 'Subscriptions by Category',
        description: 'Group subscriptions by type',
        icon: BarChart3,
        category: 'analysis',
        prompt: 'Organize all subscriptions by category (AI Services, Development Tools, etc.) and show cost breakdown for each category.',
        keywords: ['category', 'organize', 'group', 'breakdown'],
        priority: 7,
        domain: 'subscriptions'
      }
    ];
  }

  /**
   * Dashboard domain actions
   */
  private getDashboardActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'dashboard-overview',
        label: 'System Overview',
        description: 'Get a high-level system summary',
        icon: Activity,
        category: 'analysis',
        permission: 'canSeeAdminData',
        prompt: 'Provide a comprehensive system overview including user activity, task completion rates, system health, and key metrics.',
        keywords: ['overview', 'summary', 'dashboard', 'metrics'],
        priority: 8,
        domain: 'dashboard'
      },
      {
        id: 'dashboard-quick-navigation',
        label: 'Quick Navigation Help',
        description: 'Find your way around the admin panel',
        icon: Search,
        category: 'navigation',
        prompt: 'Help me navigate the admin panel. What sections are available and what can I do in each one?',
        keywords: ['navigation', 'help', 'guide', 'sections'],
        priority: 7,
        domain: 'dashboard'
      }
    ];
  }

  /**
   * Audit domain actions
   */
  private getAuditActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'audit-recent-changes',
        label: 'Recent System Changes',
        description: 'View recent audit log entries',
        icon: FileText,
        category: 'analysis',
        permission: 'canViewAuditLogs',
        prompt: 'Show me the most recent system changes from the audit log. Include who made the change, what changed, and when.',
        keywords: ['audit', 'changes', 'logs', 'history'],
        priority: 8,
        domain: 'audit'
      },
      {
        id: 'audit-user-activity',
        label: 'User Activity Report',
        description: 'Generate user activity summary',
        icon: Users,
        category: 'analysis',
        permission: 'canViewAuditLogs',
        prompt: 'Generate a user activity report showing recent actions by user. Include timestamps and action types.',
        keywords: ['activity', 'report', 'users', 'actions'],
        priority: 7,
        domain: 'audit'
      }
    ];
  }

  /**
   * Time Management domain actions
   */
  private getTimeManagementActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'time-productivity-insights',
        label: 'Productivity Insights',
        description: 'Analyze time usage and productivity',
        icon: TrendingUp,
        category: 'analysis',
        prompt: 'Analyze my time blocks and productivity patterns. Identify peak productivity hours and suggest optimizations.',
        keywords: ['productivity', 'insights', 'time', 'analysis'],
        priority: 8,
        domain: 'time-management'
      },
      {
        id: 'time-blocks-overview',
        label: 'Time Blocks Overview',
        description: 'View scheduled time blocks',
        icon: Clock,
        category: 'navigation',
        prompt: 'Show me all my scheduled time blocks for this week. Identify any gaps or overlaps.',
        keywords: ['time blocks', 'schedule', 'calendar', 'overview'],
        priority: 7,
        domain: 'time-management'
      }
    ];
  }

  // ============================================
  // NEW DOMAIN ACTIONS - Sales, Community, etc.
  // ============================================

  /**
   * Sales Portal domain actions
   */
  private getSalesPortalActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'sales-pipeline-overview',
        label: 'Pipeline Overview',
        description: 'View current deal pipeline with stage breakdown',
        icon: TrendingUp,
        category: 'analysis',
        prompt: 'Show me my current sales pipeline with deals by stage, total value, and expected close dates. Highlight any stalled deals.',
        keywords: ['pipeline', 'deals', 'stages', 'sales'],
        priority: 9,
        domain: 'sales-portal',
        isNew: true
      },
      {
        id: 'sales-commission-summary',
        label: 'Commission Summary',
        description: 'View earned and pending commissions',
        icon: DollarSign,
        category: 'analysis',
        prompt: 'Show me my commission summary including earned, pending, and projected commissions. Break down by deal and time period.',
        keywords: ['commission', 'earnings', 'payout', 'revenue'],
        priority: 8,
        domain: 'sales-portal'
      },
      {
        id: 'sales-team-performance',
        label: 'Team Performance',
        description: 'Compare team member sales metrics',
        icon: Users,
        category: 'analysis',
        permission: 'canSeeAdminData',
        prompt: 'Show team sales performance metrics. Include individual rankings, conversion rates, and deal velocity. Identify top performers.',
        keywords: ['team', 'performance', 'metrics', 'comparison'],
        priority: 7,
        domain: 'sales-portal'
      },
      {
        id: 'sales-deals-at-risk',
        label: 'Deals at Risk',
        description: 'Identify deals that need attention',
        icon: AlertTriangle,
        category: 'diagnostic',
        prompt: 'Identify deals at risk of being lost. Look for stalled deals, approaching deadlines, or deals with no recent activity. Suggest recovery actions.',
        keywords: ['risk', 'stalled', 'attention', 'urgent'],
        priority: 9,
        domain: 'sales-portal'
      },
      {
        id: 'sales-forecast',
        label: 'Revenue Forecast',
        description: 'Project upcoming revenue',
        icon: LineChart,
        category: 'analysis',
        prompt: 'Generate a revenue forecast based on current pipeline. Show expected revenue by month and quarter with confidence levels.',
        keywords: ['forecast', 'revenue', 'projection', 'prediction'],
        priority: 7,
        domain: 'sales-portal'
      },
      {
        id: 'sales-activity-log',
        label: 'Recent Sales Activity',
        description: 'View recent sales actions and updates',
        icon: Activity,
        category: 'navigation',
        prompt: 'Show recent sales activity including new deals, stage changes, won/lost updates, and team actions.',
        keywords: ['activity', 'recent', 'updates', 'log'],
        priority: 6,
        domain: 'sales-portal'
      }
    ];
  }

  /**
   * Clients/CRM domain actions
   */
  private getClientsActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'clients-overview',
        label: 'Client Overview',
        description: 'Summary of all clients and relationships',
        icon: Building2,
        category: 'analysis',
        prompt: 'Provide an overview of all clients including total count, active vs inactive, recent additions, and key account metrics.',
        keywords: ['clients', 'overview', 'summary', 'accounts'],
        priority: 8,
        domain: 'clients'
      },
      {
        id: 'clients-needs-attention',
        label: 'Clients Needing Attention',
        description: 'Find clients requiring follow-up',
        icon: AlertTriangle,
        category: 'diagnostic',
        prompt: 'Identify clients that need attention - no recent contact, pending tasks, upcoming renewals, or at-risk relationships. Suggest next actions.',
        keywords: ['attention', 'follow-up', 'neglected', 'at-risk'],
        priority: 9,
        domain: 'clients'
      },
      {
        id: 'clients-recent-activity',
        label: 'Client Activity Feed',
        description: 'Recent interactions and updates',
        icon: Activity,
        category: 'navigation',
        prompt: 'Show recent client activity including new clients, interactions, deal updates, and support tickets.',
        keywords: ['activity', 'recent', 'interactions', 'updates'],
        priority: 7,
        domain: 'clients'
      },
      {
        id: 'clients-search',
        label: 'Find a Client',
        description: 'Search and lookup client information',
        icon: Search,
        category: 'navigation',
        prompt: 'Help me find a client. I can search by name, company, email, or other details. What would you like to search for?',
        keywords: ['search', 'find', 'lookup', 'query'],
        priority: 7,
        domain: 'clients'
      },
      {
        id: 'clients-add-new',
        label: 'Add New Client',
        description: 'Guide for adding a new client',
        icon: UserPlus,
        category: 'creation',
        prompt: 'Help me add a new client to the system. What information should I include for a complete client profile?',
        keywords: ['add', 'new', 'create', 'client'],
        priority: 6,
        domain: 'clients'
      }
    ];
  }

  /**
   * Opportunities domain actions
   */
  private getOpportunitiesActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'opportunities-pipeline',
        label: 'Opportunity Pipeline',
        description: 'View all opportunities by stage',
        icon: Target,
        category: 'analysis',
        prompt: 'Show my opportunity pipeline with deals organized by stage. Include total value, probability, and expected close dates.',
        keywords: ['pipeline', 'opportunities', 'stages', 'deals'],
        priority: 9,
        domain: 'opportunities'
      },
      {
        id: 'opportunities-hot-deals',
        label: 'Hot Opportunities',
        description: 'High-value deals closing soon',
        icon: Flame,
        category: 'analysis',
        prompt: 'Show hot opportunities - high value deals with good probability closing in the next 30 days. Help me prioritize my focus.',
        keywords: ['hot', 'closing', 'priority', 'high-value'],
        priority: 9,
        domain: 'opportunities'
      },
      {
        id: 'opportunities-create',
        label: 'Create Opportunity',
        description: 'Start a new opportunity/deal',
        icon: Plus,
        category: 'creation',
        prompt: 'Help me create a new opportunity. Guide me through the required fields and best practices for deal setup.',
        keywords: ['create', 'new', 'add', 'opportunity'],
        priority: 7,
        domain: 'opportunities'
      },
      {
        id: 'opportunities-analysis',
        label: 'Win/Loss Analysis',
        description: 'Analyze deal outcomes and patterns',
        icon: PieChart,
        category: 'analysis',
        prompt: 'Analyze my win/loss patterns. What factors correlate with successful deals? Identify areas for improvement.',
        keywords: ['analysis', 'win', 'loss', 'patterns'],
        priority: 6,
        domain: 'opportunities'
      }
    ];
  }

  /**
   * Team domain actions
   */
  private getTeamActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'team-hierarchy',
        label: 'Team Structure',
        description: 'View team hierarchy and territories',
        icon: Network,
        category: 'navigation',
        permission: 'canSeeAdminData',
        prompt: 'Show me the team hierarchy including managers, territories, and reporting structure. Include member counts per level.',
        keywords: ['hierarchy', 'structure', 'org', 'territories'],
        priority: 8,
        domain: 'team'
      },
      {
        id: 'team-workload',
        label: 'Team Workload',
        description: 'Analyze team capacity and assignments',
        icon: BarChart3,
        category: 'analysis',
        permission: 'canSeeAdminData',
        prompt: 'Analyze team workload distribution. Show task counts, deadlines, and capacity per team member. Identify overloaded or underutilized resources.',
        keywords: ['workload', 'capacity', 'assignments', 'balance'],
        priority: 8,
        domain: 'team'
      },
      {
        id: 'team-add-member',
        label: 'Add Team Member',
        description: 'Invite someone to the team',
        icon: UserPlus,
        category: 'creation',
        permission: 'canManageUsers',
        prompt: 'Help me add a new team member. What information do I need to invite them and what role should they have?',
        keywords: ['add', 'invite', 'member', 'user'],
        priority: 6,
        domain: 'team'
      }
    ];
  }

  /**
   * Gamification domain actions (leaderboards, achievements, seasons)
   */
  private getGamificationActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'gamification-my-progress',
        label: 'My Progress & XP',
        description: 'View your level, XP, and achievements',
        icon: Trophy,
        category: 'analysis',
        prompt: 'Show my gamification progress including current XP, level, recent achievements, and distance to next level. What can I do to earn more XP?',
        keywords: ['progress', 'xp', 'level', 'achievements'],
        priority: 9,
        domain: 'gamification',
        isNew: true
      },
      {
        id: 'gamification-leaderboard',
        label: 'View Leaderboard',
        description: 'See current rankings',
        icon: Award,
        category: 'navigation',
        prompt: 'Show the current leaderboard. Where do I rank? Who are the top performers this week/month/season?',
        keywords: ['leaderboard', 'rankings', 'top', 'position'],
        priority: 8,
        domain: 'gamification'
      },
      {
        id: 'gamification-achievements',
        label: 'Available Achievements',
        description: 'Discover badges you can earn',
        icon: Star,
        category: 'navigation',
        prompt: 'Show available achievements I can earn. Which ones am I closest to? What actions unlock them?',
        keywords: ['achievements', 'badges', 'earn', 'unlock'],
        priority: 7,
        domain: 'gamification'
      },
      {
        id: 'gamification-streaks',
        label: 'My Streaks',
        description: 'Track your activity streaks',
        icon: Flame,
        category: 'analysis',
        prompt: 'Show my current activity streaks. What streaks are active? What do I need to do to maintain them?',
        keywords: ['streaks', 'consistency', 'daily', 'maintain'],
        priority: 8,
        domain: 'gamification'
      },
      {
        id: 'gamification-season',
        label: 'Season Progress',
        description: 'View current season challenges',
        icon: Calendar,
        category: 'analysis',
        prompt: 'Show current season details including active challenges, my progress, remaining time, and rewards I can earn.',
        keywords: ['season', 'challenges', 'rewards', 'competition'],
        priority: 7,
        domain: 'gamification'
      },
      {
        id: 'gamification-quick-wins',
        label: 'Quick XP Opportunities',
        description: 'Find easy ways to earn XP',
        icon: Zap,
        category: 'analysis',
        prompt: 'What are the quickest ways I can earn XP right now? Show low-effort actions that give rewards.',
        keywords: ['quick', 'easy', 'fast', 'earn'],
        priority: 8,
        domain: 'gamification'
      }
    ];
  }

  /**
   * Community domain actions
   */
  private getCommunityActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'community-trending',
        label: 'Trending Discussions',
        description: 'See popular posts and topics',
        icon: TrendingUp,
        category: 'navigation',
        prompt: 'Show trending discussions and popular posts in the community. What topics are people engaged with?',
        keywords: ['trending', 'popular', 'discussions', 'hot'],
        priority: 8,
        domain: 'community',
        isNew: true
      },
      {
        id: 'community-my-feed',
        label: 'My Feed',
        description: 'View posts from people you follow',
        icon: MessageSquare,
        category: 'navigation',
        prompt: 'Show my personalized community feed including posts from people I follow and topics I\'m interested in.',
        keywords: ['feed', 'posts', 'following', 'updates'],
        priority: 8,
        domain: 'community'
      },
      {
        id: 'community-create-post',
        label: 'Create Post',
        description: 'Share something with the community',
        icon: Plus,
        category: 'creation',
        prompt: 'Help me create a community post. What makes a great post that gets engagement? Any tips for sharing success stories?',
        keywords: ['create', 'post', 'share', 'new'],
        priority: 7,
        domain: 'community'
      },
      {
        id: 'community-success-stories',
        label: 'Success Stories',
        description: 'Read inspiring wins from the community',
        icon: Award,
        category: 'navigation',
        prompt: 'Show recent success stories from the community. What wins are people celebrating? How can I learn from their strategies?',
        keywords: ['success', 'wins', 'stories', 'inspiration'],
        priority: 7,
        domain: 'community'
      },
      {
        id: 'community-find-people',
        label: 'Find People',
        description: 'Discover community members',
        icon: Users,
        category: 'navigation',
        prompt: 'Help me find interesting people in the community. Show active members, top contributors, or people with similar interests.',
        keywords: ['find', 'people', 'members', 'connect'],
        priority: 6,
        domain: 'community'
      },
      {
        id: 'community-engagement',
        label: 'My Engagement Stats',
        description: 'View your community activity',
        icon: Heart,
        category: 'analysis',
        prompt: 'Show my community engagement stats including posts, comments, likes given/received, and growth over time.',
        keywords: ['engagement', 'stats', 'activity', 'likes'],
        priority: 6,
        domain: 'community'
      }
    ];
  }

  /**
   * Analytics/Progress Reports domain actions
   */
  private getAnalyticsActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'analytics-productivity',
        label: 'Productivity Report',
        description: 'Analyze your productivity metrics',
        icon: LineChart,
        category: 'analysis',
        prompt: 'Generate a productivity report showing task completion rates, time utilization, and trends over the past week/month.',
        keywords: ['productivity', 'report', 'metrics', 'performance'],
        priority: 8,
        domain: 'analytics'
      },
      {
        id: 'analytics-progress-summary',
        label: 'Progress Summary',
        description: 'Overview of goals and milestones',
        icon: Target,
        category: 'analysis',
        prompt: 'Show my progress summary including goals achieved, milestones reached, and areas needing attention.',
        keywords: ['progress', 'goals', 'summary', 'milestones'],
        priority: 8,
        domain: 'analytics'
      },
      {
        id: 'analytics-generate-report',
        label: 'Generate Report',
        description: 'Create a custom report',
        icon: FileText,
        category: 'creation',
        permission: 'canExportData',
        prompt: 'Help me generate a report. What metrics should I include? What time period? Should it include charts?',
        keywords: ['generate', 'report', 'custom', 'export'],
        priority: 7,
        domain: 'analytics'
      },
      {
        id: 'analytics-team-insights',
        label: 'Team Insights',
        description: 'Analytics for team performance',
        icon: Users,
        category: 'analysis',
        permission: 'canSeeAdminData',
        prompt: 'Show team analytics including productivity by member, collaboration patterns, and bottlenecks.',
        keywords: ['team', 'insights', 'analytics', 'performance'],
        priority: 7,
        domain: 'analytics'
      }
    ];
  }

  /**
   * Accounting domain actions
   */
  private getAccountingActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'accounting-overview',
        label: 'Financial Overview',
        description: 'Summary of accounts and transactions',
        icon: DollarSign,
        category: 'analysis',
        prompt: 'Provide a financial overview including account balances, recent transactions, and any anomalies to review.',
        keywords: ['financial', 'overview', 'accounts', 'summary'],
        priority: 8,
        domain: 'accounting'
      },
      {
        id: 'accounting-transactions',
        label: 'Recent Transactions',
        description: 'View recent financial activity',
        icon: CreditCard,
        category: 'navigation',
        prompt: 'Show recent transactions across all accounts. Highlight any that need review or categorization.',
        keywords: ['transactions', 'recent', 'payments', 'activity'],
        priority: 7,
        domain: 'accounting'
      },
      {
        id: 'accounting-reconcile',
        label: 'Reconciliation Help',
        description: 'Assistance with account reconciliation',
        icon: CheckSquare,
        category: 'diagnostic',
        prompt: 'Help me reconcile accounts. Show any discrepancies, unmatched transactions, or items needing attention.',
        keywords: ['reconcile', 'match', 'verify', 'discrepancy'],
        priority: 8,
        domain: 'accounting'
      }
    ];
  }

  /**
   * Docs/Content domain actions
   */
  private getDocsActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'docs-search',
        label: 'Search Documentation',
        description: 'Find information in the docs',
        icon: Search,
        category: 'navigation',
        prompt: 'Help me find information in the documentation. What topic are you looking for?',
        keywords: ['search', 'find', 'documentation', 'help'],
        priority: 8,
        domain: 'docs'
      },
      {
        id: 'docs-getting-started',
        label: 'Getting Started Guide',
        description: 'Quick start and onboarding',
        icon: BookOpen,
        category: 'navigation',
        prompt: 'Show me the getting started guide. What are the essential first steps for new users?',
        keywords: ['getting started', 'onboarding', 'beginner', 'guide'],
        priority: 7,
        domain: 'docs'
      },
      {
        id: 'docs-faq',
        label: 'Frequently Asked Questions',
        description: 'Common questions and answers',
        icon: HelpCircle,
        category: 'navigation',
        prompt: 'Show frequently asked questions. What are the most common issues and their solutions?',
        keywords: ['faq', 'questions', 'common', 'answers'],
        priority: 6,
        domain: 'docs'
      },
      {
        id: 'docs-changelog',
        label: 'Recent Changes',
        description: 'What\'s new in the platform',
        icon: FileText,
        category: 'navigation',
        prompt: 'Show recent changes and updates to the platform. What new features have been added?',
        keywords: ['changelog', 'updates', 'new', 'features'],
        priority: 6,
        domain: 'docs'
      }
    ];
  }

  /**
   * Attachments/Storage domain actions
   */
  private getAttachmentsActions(_agentContext: AgentContext): QuickAction[] {
    return [
      {
        id: 'attachments-recent',
        label: 'Recent Files',
        description: 'View recently uploaded files',
        icon: Folder,
        category: 'navigation',
        prompt: 'Show my recently uploaded files and attachments. Include file type, size, and which item they\'re attached to.',
        keywords: ['recent', 'files', 'uploads', 'attachments'],
        priority: 7,
        domain: 'attachments'
      },
      {
        id: 'attachments-storage',
        label: 'Storage Usage',
        description: 'Check storage quota and usage',
        icon: Database,
        category: 'analysis',
        prompt: 'Show my storage usage including total used, remaining quota, and largest files. Any recommendations for cleanup?',
        keywords: ['storage', 'quota', 'usage', 'space'],
        priority: 7,
        domain: 'attachments'
      },
      {
        id: 'attachments-cleanup',
        label: 'Storage Cleanup',
        description: 'Find files to delete or archive',
        icon: FileSearch,
        category: 'diagnostic',
        prompt: 'Help me clean up storage. Find duplicate files, old attachments, or large files that could be archived.',
        keywords: ['cleanup', 'delete', 'archive', 'organize'],
        priority: 6,
        domain: 'attachments'
      }
    ];
  }

  /**
   * Get state-based actions (reactive to system state)
   */
  private getStateBasedActions(systemState: SystemState, agentContext: AgentContext): QuickAction[] {
    const actions: QuickAction[] = [];

    // =========================================
    // TASK STATE ACTIONS
    // =========================================

    // Overdue tasks action with count (HIGHEST PRIORITY)
    if (systemState.overdueTasks && systemState.overdueTasks > 0 && agentContext.capabilities.canCreateTodos) {
      actions.push({
        id: 'state-overdue-tasks',
        label: `🔴 ${systemState.overdueTasks} Overdue Tasks`,
        description: 'Address overdue tasks immediately',
        icon: AlertTriangle,
        category: 'diagnostic',
        permission: 'canCreateTodos',
        prompt: `I have ${systemState.overdueTasks} overdue tasks. Show them to me with priority levels and suggest a recovery plan to get back on track.`,
        keywords: ['overdue', 'urgent', 'late'],
        priority: 10, // Highest priority
        estimatedTime: '< 5 seconds'
      });
    }

    // Tasks due today
    if (systemState.tasksDueToday && systemState.tasksDueToday > 0 && agentContext.capabilities.canCreateTodos) {
      actions.push({
        id: 'state-tasks-due-today',
        label: `📅 ${systemState.tasksDueToday} Due Today`,
        description: 'View tasks due today',
        icon: CheckSquare,
        category: 'navigation',
        permission: 'canCreateTodos',
        prompt: `I have ${systemState.tasksDueToday} tasks due today. Show them in priority order and help me plan my day.`,
        keywords: ['today', 'due', 'tasks'],
        priority: 9
      });
    }

    // =========================================
    // CALENDAR STATE ACTIONS
    // =========================================

    // Calendar sync error
    if (systemState.calendarSyncStatus === 'error') {
      actions.push({
        id: 'state-calendar-error',
        label: '❌ Calendar Sync Error',
        description: 'Fix calendar synchronization issues',
        icon: AlertTriangle,
        category: 'diagnostic',
        prompt: 'My calendar sync has errors. Run a diagnostic and help me troubleshoot the issue. Check permissions, OAuth tokens, and API access.',
        keywords: ['error', 'sync', 'calendar', 'fix'],
        priority: 10,
        estimatedTime: '10-15 seconds'
      });
    } else if (systemState.calendarSyncStatus === 'warning') {
      actions.push({
        id: 'state-calendar-warning',
        label: '⚠️ Calendar Sync Warning',
        description: 'Check calendar sync warnings',
        icon: AlertTriangle,
        category: 'diagnostic',
        prompt: 'My calendar sync has warnings. What issues were detected and how can I resolve them?',
        keywords: ['warning', 'sync', 'calendar'],
        priority: 8
      });
    }

    // Upcoming events
    if (systemState.upcomingEvents && systemState.upcomingEvents > 0) {
      actions.push({
        id: 'state-upcoming-events',
        label: `📆 ${systemState.upcomingEvents} Upcoming Events`,
        description: 'Review your upcoming schedule',
        icon: Calendar,
        category: 'navigation',
        prompt: `I have ${systemState.upcomingEvents} upcoming events. Show them organized by day with meeting links and participant information.`,
        keywords: ['upcoming', 'events', 'schedule'],
        priority: 7
      });
    }

    // =========================================
    // SALES STATE ACTIONS
    // =========================================

    // Deals at risk (high priority alert)
    if (systemState.dealsAtRisk && systemState.dealsAtRisk > 0) {
      actions.push({
        id: 'state-deals-at-risk',
        label: `🚨 ${systemState.dealsAtRisk} Deals at Risk`,
        description: 'Stalled deals need attention',
        icon: AlertTriangle,
        category: 'diagnostic',
        prompt: `I have ${systemState.dealsAtRisk} deals at risk (no activity in 30+ days). Show me these deals and suggest re-engagement strategies.`,
        keywords: ['deals', 'risk', 'stalled', 'sales'],
        priority: 10,
        domain: 'sales-portal',
        isNew: true
      });
    }

    // Pipeline value (informational)
    if (systemState.pipelineValue && systemState.pipelineValue > 0) {
      const formattedValue = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(systemState.pipelineValue);
      
      actions.push({
        id: 'state-pipeline-value',
        label: `💰 ${formattedValue} Pipeline`,
        description: 'View your current pipeline value',
        icon: DollarSign,
        category: 'analysis',
        prompt: `My current pipeline is worth ${formattedValue}. Show me the breakdown by stage, top deals, and forecast for the next quarter.`,
        keywords: ['pipeline', 'value', 'deals', 'revenue'],
        priority: 6,
        domain: 'sales-portal'
      });
    }

    // =========================================
    // GAMIFICATION STATE ACTIONS
    // =========================================

    // Streak at risk (urgent!)
    if (systemState.streakAtRisk && systemState.currentStreak && systemState.currentStreak > 0) {
      actions.push({
        id: 'state-streak-at-risk',
        label: `🔥 ${systemState.currentStreak}-Day Streak at Risk!`,
        description: 'Complete an action to keep your streak',
        icon: Flame,
        category: 'diagnostic',
        prompt: `My ${systemState.currentStreak}-day streak is at risk! What's the quickest action I can take right now to maintain it?`,
        keywords: ['streak', 'risk', 'urgent', 'gamification'],
        priority: 10,
        domain: 'gamification',
        isNew: true
      });
    } else if (systemState.currentStreak && systemState.currentStreak > 0) {
      // Show current streak (motivational)
      actions.push({
        id: 'state-current-streak',
        label: `🔥 ${systemState.currentStreak}-Day Streak`,
        description: 'Keep your momentum going!',
        icon: Flame,
        category: 'analysis',
        prompt: `I'm on a ${systemState.currentStreak}-day streak! Show my streak history and suggest ways to maintain it.`,
        keywords: ['streak', 'progress', 'gamification'],
        priority: 5,
        domain: 'gamification'
      });
    }

    // Achievements close to unlock
    if (systemState.pendingAchievements && systemState.pendingAchievements > 0) {
      actions.push({
        id: 'state-pending-achievements',
        label: `🏆 ${systemState.pendingAchievements} Achievements Almost Unlocked`,
        description: 'You\'re close to earning new badges!',
        icon: Award,
        category: 'navigation',
        prompt: `I have ${systemState.pendingAchievements} achievements that are 80%+ complete. Show me what I need to do to unlock them.`,
        keywords: ['achievements', 'badges', 'close', 'earn'],
        priority: 7,
        domain: 'gamification'
      });
    }

    return actions;
  }

  /**
   * Universal actions available everywhere
   */
  /**
   * Universal actions that are ALWAYS available regardless of context
   * These provide baseline functionality and help users get started
   */
  private getUniversalActions(_agentContext: AgentContext): QuickAction[] {
    return [
      // High-priority always-visible actions
      {
        id: 'universal-help-me-start',
        label: '💡 Help Me Get Started',
        description: 'Get personalized guidance for what you can do here',
        icon: Lightbulb,
        category: 'navigation',
        prompt: 'Help me understand what I can do on this page. Explain the key features and show me how to get started with the most common tasks.',
        keywords: ['help', 'start', 'guide', 'intro', 'tutorial'],
        priority: 10, // High priority - always show first
        isNew: false
      },
      {
        id: 'universal-create-task',
        label: '✨ Create a Task',
        description: 'Quickly create a new task or reminder',
        icon: Plus,
        category: 'creation',
        permission: 'canCreateTodos',
        prompt: 'Help me create a new task. Ask me for the title, description, priority, and when it needs to be done.',
        keywords: ['create', 'task', 'new', 'todo', 'add'],
        priority: 9,
        isNew: false
      },
      {
        id: 'universal-whats-today',
        label: '📅 What\'s On Today',
        description: 'See everything scheduled for today',
        icon: Calendar,
        category: 'analysis',
        prompt: 'Give me a complete overview of today - tasks due, calendar events, priorities, and what needs my attention most.',
        keywords: ['today', 'overview', 'summary', 'daily', 'schedule'],
        priority: 9,
        isNew: false
      },
      {
        id: 'universal-search',
        label: '🔍 Search Everything',
        description: 'Find anything across the system',
        icon: Search,
        category: 'navigation',
        prompt: 'I need to search for something. What would you like me to help you find? I can search tasks, clients, projects, and more.',
        keywords: ['search', 'find', 'lookup', 'query'],
        priority: 8,
        isNew: false
      },
      {
        id: 'universal-quick-braindump',
        label: '🧠 Brain Dump',
        description: 'Capture ideas quickly and let AI organize them',
        icon: Brain,
        category: 'creation',
        prompt: 'I want to do a brain dump. Help me capture all my thoughts, ideas, and tasks, then organize them intelligently.',
        keywords: ['braindump', 'ideas', 'capture', 'organize'],
        priority: 8,
        isNew: false
      },
      {
        id: 'universal-help',
        label: 'Documentation',
        description: 'View help docs for this page',
        icon: FileText,
        category: 'navigation',
        prompt: 'Show me the documentation for this page. What features are available and how do I use them?',
        keywords: ['help', 'docs', 'guide', 'documentation'],
        priority: 5
      },
      {
        id: 'universal-shortcuts',
        label: 'Keyboard Shortcuts',
        description: 'Learn keyboard shortcuts',
        icon: Zap,
        category: 'navigation',
        prompt: 'Show me all available keyboard shortcuts for this section. Help me work more efficiently.',
        keywords: ['shortcuts', 'keyboard', 'hotkeys', 'efficiency'],
        priority: 4
      }
    ];
  }

  /**
   * General domain actions (fallback for pages that don't match specific domains)
   */
  private getGeneralActions(agentContext: AgentContext): QuickAction[] {
    const actions: QuickAction[] = [
      {
        id: 'general-navigate-admin',
        label: 'Go to Admin Dashboard',
        description: 'Navigate to the admin dashboard',
        icon: LayoutDashboard,
        category: 'navigation',
        permission: 'canSeeAdminData',
        prompt: 'Take me to the admin dashboard where I can see an overview of all system features.',
        keywords: ['admin', 'dashboard', 'navigate', 'overview'],
        priority: 8,
        domain: 'general'
      },
      {
        id: 'general-create-task',
        label: 'Create New Task',
        description: 'Quickly create a new task',
        icon: Plus,
        category: 'creation',
        permission: 'canCreateTodos',
        prompt: 'Help me create a new task. I need to set a title, description, priority, and due date.',
        keywords: ['create', 'task', 'new', 'todo'],
        priority: 7,
        domain: 'general'
      },
      {
        id: 'general-today-overview',
        label: "Today's Overview",
        description: 'Get a summary of what\'s happening today',
        icon: Calendar,
        category: 'analysis',
        prompt: 'Give me an overview of everything happening today - tasks due, calendar events, and priorities.',
        keywords: ['today', 'overview', 'summary', 'daily'],
        priority: 6,
        domain: 'general'
      },
      {
        id: 'general-search',
        label: 'Search Everything',
        description: 'Search across all content',
        icon: Search,
        category: 'navigation',
        prompt: 'Help me search for something. I need to find tasks, documents, or other content.',
        keywords: ['search', 'find', 'lookup', 'content'],
        priority: 5,
        domain: 'general'
      }
    ];

    // Add user-specific actions based on role
    if (agentContext.capabilities.canSeeAdminData) {
      actions.push({
        id: 'general-system-status',
        label: 'System Status',
        description: 'Check system health and status',
        icon: Activity,
        category: 'diagnostic',
        prompt: 'Show me the current system status, including any issues, upcoming maintenance, or alerts.',
        keywords: ['system', 'status', 'health', 'monitor'],
        priority: 7,
        domain: 'general'
      });
    }

    return actions;
  }

  /**
   * Filter actions based on user permissions
   */
  private filterByPermissions(actions: QuickAction[], agentContext: AgentContext): QuickAction[] {
    return actions.filter(action => {
      // No permission required = available to everyone
      if (!action.permission) {return true;}

      // Check if user has the required capability
      return agentContext.capabilities[action.permission] === true;
    });
  }

  /**
   * Group actions by category
   */
  groupActionsByCategory(actions: QuickAction[]): QuickActionGroup[] {
    const categories: Record<string, QuickActionGroup> = {
      diagnostic: {
        category: 'diagnostic',
        label: 'Diagnostics & Troubleshooting',
        description: 'Check system health and fix issues',
        icon: Activity,
        actions: []
      },
      navigation: {
        category: 'navigation',
        label: 'Navigation & Help',
        description: 'Find your way around and get help',
        icon: Search,
        actions: []
      },
      creation: {
        category: 'creation',
        label: 'Create & Build',
        description: 'Start new tasks, workflows, and more',
        icon: Plus,
        actions: []
      },
      analysis: {
        category: 'analysis',
        label: 'Analysis & Insights',
        description: 'Review data and get insights',
        icon: BarChart3,
        actions: []
      },
      management: {
        category: 'management',
        label: 'Management & Admin',
        description: 'Manage users, settings, and permissions',
        icon: Settings,
        actions: []
      },
      productivity: {
        category: 'productivity',
        label: 'Productivity',
        description: 'Boost your efficiency',
        icon: Zap,
        actions: []
      }
    };

    // Group actions by category
    actions.forEach(action => {
      const category = categories[action.category];
      if (category) {
        category.actions.push(action);
      }
    });

    // Return only categories that have actions
    return Object.values(categories).filter(cat => cat.actions.length > 0);
  }

  /**
   * Search actions by keyword
   */
  searchActions(actions: QuickAction[], query: string): QuickAction[] {
    const normalizedQuery = query.toLowerCase().trim();
    
    if (!normalizedQuery) {return actions;}

    return actions.filter(action => {
      // Search in label
      if (action.label.toLowerCase().includes(normalizedQuery)) {return true;}
      
      // Search in description
      if (action.description.toLowerCase().includes(normalizedQuery)) {return true;}
      
      // Search in keywords
      if (action.keywords.some(keyword => keyword.toLowerCase().includes(normalizedQuery))) {return true;}

      // Search in domain
      if (action.domain && action.domain.toLowerCase().includes(normalizedQuery)) {return true;}
      
      return false;
    });
  }

  /**
   * Get unique domains from a set of actions
   */
  getUniqueDomains(actions: QuickAction[]): string[] {
    const domains = new Set<string>();
    actions.forEach(action => {
      if (action.domain) {
        domains.add(action.domain);
      }
    });
    return Array.from(domains).sort();
  }

  /**
   * Filter actions by domain
   */
  filterByDomain(actions: QuickAction[], domain: string | null): QuickAction[] {
    if (!domain) {return actions;}
    return actions.filter(action => action.domain === domain);
  }
}

export const quickActionsService = new QuickActionsService();
